import React, { useEffect, useMemo, useRef, useState } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import { Search, ChevronDown, Check, Star, Globe, Clock } from "lucide-react";
import { getLS } from '../helpers/utils';


const AlacritySearchDropdown = ({
                                  options,
                                  value,
                                  onChange,
                                  placeholder,
                                  popular = [],
                                  showCode = false,
                                  className,
                                  btnDropDown,
                                  t,
                                  id,
                                  title,
                                  renderOptionCustomized = true,
                                  filterAttribute
                                }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);


  useEffect(() => {
    const selected = options.find(opt => opt.value === value);
    setSelectedOption(selected || null);
  }, [value, options]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  const filteredOptions = options.filter(opt => {
    const s = (search || "").toLowerCase();
    const label = (opt.label || "").toLowerCase();
    const value = (opt.value || "").toLowerCase();

    return label.includes(s) || value.includes(s) ;
  });

  const popularOptions = popular.length > 0
    ? options.filter(opt => popular.includes(opt.value))
    : [];

  const handleSelect = (option) => {
    setSelectedOption(option);
    onChange(option.value, filterAttribute);
    setIsOpen(false);
    setSearch('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    const totalOptions = search ? filteredOptions : [...popularOptions, ...options];

    // eslint-disable-next-line default-case
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < totalOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < totalOptions.length) {
          handleSelect(totalOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const isDark = getLS('dark') === 'true';
  const base = className ? 'icon-menu form-control' : 'filter text-filter form-control';
  const finalClass = isDark ? `${base} form-control-dark` : base;


  if (btnDropDown) {
    const optionsBtnDropDown = (search ? filteredOptions : options).map((item, i) => (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/click-events-have-key-events,react/jsx-key,react/no-array-index-key
      <li key={i} className="dropdown-item" onClick={() => handleSelect(item)}>
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        {item.img && (
          // eslint-disable-next-line jsx-a11y/alt-text
          <img
            src={item.img}
            width="24px"
            style={{ marginRight: '8px' }}
          />
        )}
        {item.label}
      </li>
    ));

    return (
      <div className="form-group" ref={dropdownRef}>
        <div className="dropdown">
          <button
            className={className}
            type="button"
            id="dropdownMenuButton"
            onClick={() => setIsOpen(!isOpen)}
            aria-haspopup="true"
            aria-expanded={isOpen}
          >
            {selectedOption ? (
              <>
                {selectedOption.img && (
                  <img alt="" src={selectedOption.img} width="24px" style={{ marginRight: '8px' }} />
                )}
                {selectedOption.label}
              </>
            ) : title}
          </button>

          {isOpen && (
            <div className="dropdown-menu show" style={{ display: 'block', position: 'absolute', zIndex: 1000 }}>
              {/* Search input */}
              <div style={{ padding: '8px', borderBottom: '1px solid #e9ecef' }}>
                <div style={{ position: 'relative' }}>
                  <Search style={{ position: 'absolute', left: '8px', top: '8px', width: '16px', height: '16px', color: '#6c757d' }} />
                  <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search countries..."
                    className="form-control"
                    style={{ paddingLeft: '32px', fontSize: '14px' }}
                  />
                </div>
              </div>


              {/* Options list */}
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '200px', overflowY: 'auto' }}>
                {(search ? filteredOptions : options).map((item, i) => {
                  const actualIndex = search ? i : popularOptions.length + i;
                  return (
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/click-events-have-key-events
                    <li
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      className="dropdown-item"
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setHighlightedIndex(actualIndex)}
                      style={{
                        cursor: 'pointer',
                        // eslint-disable-next-line no-nested-ternary
                        backgroundColor: highlightedIndex === actualIndex ? '#e3f2fd' : (value === item.value ? '#f0f8ff' : 'transparent'),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {item.img && (
                          <img
                            src={item.img}
                            width="24px"
                            style={{ marginRight: '8px' }}
                            alt={item.label}
                          />
                        )}
                        {item.label}
                        {showCode && item.code && (
                          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>
                            {item.code}
                          </span>
                        )}
                      </div>
                      {value === item.value && (
                        <Check style={{ width: '16px', height: '16px', color: '#007bff' }} />
                      )}
                    </li>
                  );
                })}
              </ul>

              {search && filteredOptions.length === 0 && (
                <div style={{ padding: '32px 12px', textAlign: 'center', color: '#6c757d' }}>
                  No countries found matching &#34;{search}&#34;
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }


  return (
    <div className="form-group" ref={dropdownRef}>
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          className={finalClass}
          onClick={() => setIsOpen(!isOpen)}
          style={{
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            border: '1px solid #ccc'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {selectedOption ? (
              <>
                {selectedOption.img && (
                  <span style={{ marginRight: '8px', fontSize: '18px' }}>
                    {selectedOption.img.startsWith("/") ? (
                      <img
                        src={selectedOption.img}
                        alt={selectedOption.label}
                        style={{ width: 20, height: 14, objectFit: "cover", borderRadius: 2 }}
                      />
                    ) : (
                      selectedOption.img
                    )}
                  </span>
                )}
                <span>{selectedOption.label}</span>
              </>
            ) : (
              <span style={{ color: '#999' }}>
                {t ? t('dropdown.filter-by') : ''} {title || placeholder}
              </span>
            )}
          </div>
          <ChevronDown style={{ width: '16px', height: '16px', color: '#6c757d', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
        </button>

        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              zIndex: 1000,
              marginTop: '2px'
            }}
          >
            {/* Search input */}
            <div style={{ padding: '8px', borderBottom: '1px solid #e9ecef' }}>
              <div style={{ position: 'relative' }}>
                <Search style={{ position: 'absolute', left: '8px', top: '8px', width: '16px', height: '16px', color: '#6c757d' }} />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search countries..."
                  className="form-control"
                  style={{ paddingLeft: '32px', fontSize: '14px' }}
                />
              </div>
            </div>


            {/* Options list */}
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {(search ? filteredOptions : options).map((item, i) => {
                const actualIndex = search ? i : popularOptions.length + i;
                return (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setHighlightedIndex(actualIndex)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      // eslint-disable-next-line no-nested-ternary
                      backgroundColor: highlightedIndex === actualIndex ? '#e3f2fd' : (value === item.value ? '#f0f8ff' : 'transparent'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '14px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {item.img && (
                        <span style={{ marginRight: '8px', fontSize: '18px' }}>
                          {item.img.startsWith("/") ? (
                            <img
                              src={item.img}
                              alt={item.label}
                              style={{ width: 20, height: 14, objectFit: "cover", borderRadius: 2 }}
                            />
                          ) : (
                            item.img
                          )}
                        </span>
                      )}
                      <span>{item.label}</span>
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>
                        {item.value}
                      </span>
                    </div>
                    {value === item.value && (
                      <Check style={{ width: '16px', height: '16px', color: '#007bff' }} />
                    )}
                  </div>
                );
              })}
            </div>

            {search && filteredOptions.length === 0 && (
              <div style={{ padding: '32px 12px', textAlign: 'center', color: '#6c757d' }}>
                No countries found matching &#34;{search}&#34;
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlacritySearchDropdown;
